import { AxiosRequestConfig } from 'axios'
import { DefaultHTTPResponse } from 'common/HTTPClient/DefaultHTTPResponse'
import {
  ListDataModelSchemaEnginesRequest,
  ListDataModelSchemaEnginesResponse,
  ListDataModelSchemaReponse,
  ListDataModelSchemaRequest,
  ValidateDataModelSchemaFile,
  ValidateDataModelSchemaFileResponse,
  GenerateDataModelSchemaRequest,
  GenerateDataModelSchemaResponse,
  ListDataModelSchemaResourceRequest,
  ListDataModelSchemaResourceResponse,
  GetDataModelSchemaResourceResponse,
  CreateDataModelSchemaResourceRequestType,
  CreateDataModelSchemaResourceResponse,
  UpdateDataModelSchemaResourceRequestType,
  UpdateDataModelSchemaResourceResponse,
  PreviewDataModelSchemaResourceResponse,
} from './types'
import { to } from 'common/helpers/awaitTo'
import { del, get, post, patch } from 'common/HTTPClient'
import { PaginatedResponse } from 'common/types/kraken-core/Pagination'
import { DataModelSchemaDetail } from 'common/types/kraken-core/DataModelSchema'

export async function getDataModelSchema(
  id?: string,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<DataModelSchemaDetail>> {
  const [error, response] = await to(get(`/data_model/schemas/${id}`, { ...config }))
  if (error) throw error
  return { error, response }
}

export async function listDataModelSchema(
  data?: ListDataModelSchemaRequest,
  config?: AxiosRequestConfig
): Promise<PaginatedResponse<ListDataModelSchemaReponse>> {
  let params: any = {
    ...data,
  }

  if (data?.owner) {
    params = {
      ...params,
      owner: null,
      owner_name: data.owner.name,
    }
  }
  const [error, response] = await to(get('/data_model/schemas', { params, ...config }))
  if (error) throw error
  return response?.data
}

export async function listEngines(
  data?: ListDataModelSchemaEnginesRequest,
  config?: AxiosRequestConfig
): Promise<PaginatedResponse<ListDataModelSchemaEnginesResponse>> {
  const params = { ...data }
  const [error, response] = await to(get('/data_model/engines', { params, ...config }))
  if (error) throw error
  return response?.data
}

export async function updateDataModelSchema(
  entity: DataModelSchemaDetail,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<DataModelSchemaDetail>> {
  const [error, response] = await to(
    patch(`/data_model/schemas/${entity.id}`, entity, { ...config })
  )
  if (error) throw error
  return { error, response }
}

export async function createDataModelSchema(
  entity: DataModelSchemaDetail,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<DataModelSchemaDetail>> {
  const [error, response] = await to(post('/data_model/schemas', entity, { ...config }))
  return { error, response }
}

export async function deleteDataModelSchema(
  id: string,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<DataModelSchemaDetail>> {
  const [error, response] = await to(del(`/data_model/schemas/${id}`, { ...config }))
  return { error, response }
}

export async function validateDataModelSchemaFile(
  params: ValidateDataModelSchemaFile,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<ValidateDataModelSchemaFileResponse>> {
  const formData = new FormData()
  formData.append('file', params.file)
  const [error, response] = await to(
    post(`/data_model/schemas/${params.dataModelSchemaId}/test`, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  )
  return { error, response }
}

export async function generateDataModelSchema(
  params: GenerateDataModelSchemaRequest,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<GenerateDataModelSchemaResponse>> {
  const formData = new FormData()
  formData.append('engine', params.engine)

  if (params.prompt) {
    formData.append('prompt', params.prompt)
  }

  if (params.data_model_schema_id) {
    formData.append('data_model_schema_id', params.data_model_schema_id)
  }

  if (params.files && params.files.length > 0) {
    params.files.forEach((file, index) => {
      formData.append('files', file)
    })
  }

  const [error, response] = await to(
    post('/data_model/schemas/generate', formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  )
  return { error, response }
}

// Data Model Schema Resource Services
export async function listDataModelSchemaResources(
  data?: ListDataModelSchemaResourceRequest,
  config?: AxiosRequestConfig
): Promise<PaginatedResponse<ListDataModelSchemaResourceResponse>> {
  const params = { ...data }
  const [error, response] = await to(get('/data_model/schemas/resources', { params, ...config }))
  if (error) throw error
  return response?.data
}

export async function getDataModelSchemaResource(
  id: string,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<GetDataModelSchemaResourceResponse>> {
  const [error, response] = await to(get(`/data_model/schemas/resources/${id}`, { ...config }))
  if (error) throw error
  return { error, response }
}

export async function createDataModelSchemaResource(
  params: CreateDataModelSchemaResourceRequestType,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<CreateDataModelSchemaResourceResponse>> {
  const formData = new FormData()
  formData.append('model', params.model)
  formData.append('name', params.name)
  formData.append('file', params.file)
  formData.append('content_type', params.content_type)
  formData.append('kind', params.kind)

  if (params.encoding) {
    formData.append('encoding', params.encoding)
  }

  const [error, response] = await to(
    post('/data_model/schemas/resources', formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  )
  return { error, response }
}

export async function updateDataModelSchemaResource(
  id: string,
  params: UpdateDataModelSchemaResourceRequestType,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<UpdateDataModelSchemaResourceResponse>> {
  const formData = new FormData()

  if (params.name) {
    formData.append('name', params.name)
  }
  if (params.file) {
    formData.append('file', params.file)
  }
  if (params.content_type) {
    formData.append('content_type', params.content_type)
  }
  if (params.kind) {
    formData.append('kind', params.kind)
  }
  if (params.encoding) {
    formData.append('encoding', params.encoding)
  }

  const [error, response] = await to(
    patch(`/data_model/schemas/resources/${id}`, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  )
  return { error, response }
}

export async function deleteDataModelSchemaResource(
  id: string,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<void>> {
  const [error, response] = await to(del(`/data_model/schemas/resources/${id}`, { ...config }))
  return { error, response }
}

export async function downloadDataModelSchemaResource(
  id: string,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<Blob>> {
  const [error, response] = await to(
    get(`/data_model/schemas/resources/${id}/download`, {
      ...config,
      responseType: 'blob',
    })
  )
  return { error, response }
}

export async function previewDataModelSchemaResource(
  id: string,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<PreviewDataModelSchemaResourceResponse>> {
  const [error, response] = await to(
    get(`/data_model/schemas/resources/${id}/preview`, { ...config })
  )
  if (error) throw error
  return { error, response }
}
